import React, { useCallback, useEffect, useRef, useState } from 'react';
import './App.css';
import AuthForm from './components/AuthForm';
import ChatInterface from './components/ChatInterface';
import ChatList from './components/ChatList';
import { API_BASE_URL } from './constants';
import authUtils from './utils/authUtils';

function App() {
  const [currentChatId, setCurrentChatId] = useState(null);
  const [chats, setChats] = useState([]);
  const [loading, setLoading] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const [error, setError] = useState(null);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [userInfo, setUserInfo] = useState(null);
  const [pagination, setPagination] = useState({
    page: 1,
    limit: 20,
    total: 0,
    hasNext: false
  });
  
  // Ref to track active requests and prevent duplicate calls
  const loadingRef = useRef(false);
  const loadingMoreRef = useRef(false);
  const limitRef = useRef(20);

  // Check authentication status on component mount
  useEffect(() => {
    checkAuthStatus();
  }, []);

  const checkAuthStatus = () => {
    const authenticated = authUtils.isAuthenticated();
    setIsAuthenticated(authenticated);
    if (authenticated) {
      setUserInfo(authUtils.getUserInfo());
    }
  };

  const handleLogin = (credentials) => {
    const success = authUtils.saveCredentials(credentials);
    if (success) {
      setIsAuthenticated(true);
      setUserInfo(authUtils.getUserInfo());
      setError(null);
    } else {
      setError('Ошибка при сохранении данных авторизации');
    }
  };

  const handleLogout = () => {
    authUtils.clearCredentials();
    setIsAuthenticated(false);
    setUserInfo(null);
    setChats([]);
    setCurrentChatId(null);
    setError(null);
    setPagination({
      page: 1,
      limit: 20,
      total: 0,
      hasNext: false
    });
  };

  const loadChats = useCallback(async (page = 1, append = false) => {
    // Prevent duplicate requests
    if (append) {
      if (loadingMoreRef.current || loadingRef.current) {
        return;
      }
      loadingMoreRef.current = true;
    } else {
      if (loadingRef.current) {
        return;
      }
      loadingRef.current = true;
    }
    
    try {
      if (append) {
        setLoadingMore(true);
      } else {
        setLoading(true);
      }
      setError(null);
      
      const encodedCredentials = authUtils.getEncodedCredentials();
      if (!encodedCredentials) {
        throw new Error('No authentication credentials found');
      }
      
      const limit = limitRef.current || 20;
      
      const response = await fetch(`${API_BASE_URL}/chats/?page=${page}&limit=${limit}`, {
        headers: {
          'X-User-Credentials': encodedCredentials,
        },
      });

      if (!response.ok) {
        if (response.status === 401) {
          throw new Error('Unauthorized: Please log in again');
        } else if (response.status === 403) {
          throw new Error('Forbidden: Access denied');
        } else if (response.status >= 500) {
          throw new Error('Server error: Please try again later');
        } else {
          throw new Error(`Failed to load chats: ${response.status}`);
        }
      }

      const data = await response.json();
      
      if (!data.success) {
        throw new Error(data.detail || 'Failed to load chats');
      }
      
      const newChats = data.data.items || [];
      const meta = data.data.meta || {};
      
      if (append) {
        setChats(prev => [...prev, ...newChats]);
      } else {
        setChats(newChats);
      }
      
      setPagination(prev => {
        const newLimit = meta.limit || prev.limit || 20;
        limitRef.current = newLimit;
        return {
          page: meta.page || page,
          limit: newLimit,
          total: meta.total || 0,
          hasNext: meta.has_next || false
        };
      });
    } catch (err) {
      const errorMessage = append 
        ? `Error loading more chats: ${err.message}` 
        : `Error loading chats: ${err.message}`;
      setError(errorMessage);
      console.error('Error loading chats:', err);
    } finally {
      setLoading(false);
      setLoadingMore(false);
      loadingRef.current = false;
      loadingMoreRef.current = false;
    }
  }, []);

  useEffect(() => {
    if (pagination.limit) {
      limitRef.current = pagination.limit;
    }
  }, [pagination.limit]);

  useEffect(() => {
    if (isAuthenticated) {
      loadChats();
    }
  }, [isAuthenticated, loadChats]);

  const createNewChat = async () => {
    try {
      setLoading(true);
      setError(null);

      const encodedCredentials = authUtils.getEncodedCredentials();
      if (!encodedCredentials) {
        throw new Error('No authentication credentials found');
      }

      const response = await fetch(`${API_BASE_URL}/chats/new`, {
        method: 'POST',
        headers: {
          'X-User-Credentials': encodedCredentials,
        },
      });

      if (!response.ok) {
        throw new Error(`Failed to create chat: ${response.status}`);
      }

      const data = await response.json();
      const newChat = data.data;
      
      setChats(prev => [newChat, ...prev]);
      setCurrentChatId(newChat.id);
      
      // Update pagination total count
      setPagination(prev => ({
        ...prev,
        total: prev.total + 1
      }));
    } catch (err) {
      setError(`Error creating chat: ${err.message}`);
      console.error('Error creating chat:', err);
    } finally {
      setLoading(false);
    }
  };

  const selectChat = (chatId) => {
    setCurrentChatId(chatId);
  };

  const loadMoreChats = useCallback(() => {
    if (pagination.hasNext && !loadingMore && !loading && !loadingMoreRef.current) {
      loadChats(pagination.page + 1, true);
    }
  }, [pagination.hasNext, pagination.page, loadingMore, loading, loadChats]);

  return (
    <div className="App">
      <header className="App-header">
        <div className="header-overlay"></div>
        <div className="header-content">
        <div className="header-logo">
            <img src="/syngenta-25.png" alt="Syngenta Logo" className="logo-img" />
          </div>
          <div className="header-logo">
            <img src="/syngenta-logo.png" alt="Syngenta Logo" className="logo-img" />
          </div>
        </div>
      </header>
      
      {!isAuthenticated ? (
        <main className="App-main auth-main">
          <AuthForm 
            onLogin={handleLogin}
            onLogout={handleLogout}
            isAuthenticated={isAuthenticated}
            userInfo={userInfo}
          />
        </main>
      ) : (
        <main className="App-main">
          <div className="sidebar">
            <div className="sidebar-header">
              <button 
                onClick={createNewChat} 
                disabled={loading}
                className="new-chat-btn"
              >
                {loading ? 'Creating...' : '+ New Chat'}
              </button>
            </div>
            
            <ChatList 
              chats={chats}
              currentChatId={currentChatId}
              onSelectChat={selectChat}
              loading={loading}
              loadingMore={loadingMore}
              hasNext={pagination.hasNext}
              onLoadMore={loadMoreChats}
              pagination={pagination}
            />

            {authUtils.isAuthenticated() && (() => {
              const userInfo = authUtils.getUserInfo();
              return (
                <div className="user-info">
                  <div className="user-info-header">
                    <span className="user-name">Пользователь: {userInfo?.userId}</span>
                    <span className="user-tenant">Tenant: {userInfo?.tenant}</span>
                  </div>
                  <button 
                    onClick={() => {
                      authUtils.clearCredentials();
                      window.location.reload();
                    }}
                    className="logout-btn-header"
                    title="Выйти из системы"
                  >
                    Выйти
                  </button>
                </div>
              );
            })()}
          </div>

          <div className="chat-container">
            {error && (
              <div className="error-message">
                {error}
              </div>
            )}
            
            {currentChatId ? (
              <ChatInterface 
                chatId={currentChatId}
                onError={setError}
              />
            ) : (
              <div className="welcome-message">
                <h2>Добро пожаловать в Syngenta Chat</h2>
                <p>Создайте новый чат или выберите существующий для начала работы.</p>
                <p>Функционал приложения:</p>
                <ul>
                  <li>Создание новых чатов</li>
                  <li>Отправка сообщений</li>
                  <li>Получение ответов через SSE (Server-Sent Events)</li>
                  <li>Различные режимы чата (cropwise_read, cropwise_write, knowledge_base_read)</li>
                </ul>
              </div>
            )}
          </div>
        </main>
      )}
    </div>
  );
}

export default App;